/**
 * Splits the "recent blogs per category" payload into the home page's three
 * sections so that no post appears in more than one of them.
 *
 * All three used to be built from the same "newest post of each category" list,
 * so the newest post showed up in the hero, again in the slider beside it, and
 * a third time in Top Stories.
 *
 * Each section now takes one post per category, walking one step further down
 * that category's list each time. If the newest post site-wide is a Marketing
 * post: the hero gets Marketing #1, the slider gets Marketing #2 and Top Stories
 * gets Marketing #3. Categories that did not supply the hero are simply read
 * from the top — #1 in the slider, #2 in Top Stories.
 *
 * `recentBlogs` arrives newest-first from /recent-blogs-for-each-category.
 *
 * @param {Array}  data          the endpoint's payload
 * @param {Array}  categoryNames category names the home page features
 * @param {number} [limit]       how many categories to feature (matches the old
 *                               hard-coded cap of 4)
 * @returns {{hero: object|null, slider: Array, topStories: Array}}
 */
export const buildHomeSections = (data, categoryNames, limit = 4) => {
  const names = Array.isArray(categoryNames) ? categoryNames : [];
  const categories = (Array.isArray(data) ? data : [])
    .filter(
      (category) =>
        category?.categoryName &&
        names.some((name) => category.categoryName.includes(name))
    )
    .slice(0, limit);

  // How far into each category's list we have already handed out.
  const cursor = {};
  const take = (category) => {
    const index = cursor[category.categoryName] ?? 0;
    const blog = category.recentBlogs?.[index];
    // A category with nothing left contributes nothing — better a shorter
    // section than a repeat.
    if (!blog) return null;
    cursor[category.categoryName] = index + 1;
    return blog;
  };

  const newestOf = (category) => category.recentBlogs?.[0];
  const postedAt = (blog) =>
    blog?.createdAt ? new Date(blog.createdAt).getTime() : 0;

  // The hero is the newest post across the featured categories.
  const heroCategory = categories.reduce((best, category) => {
    if (!newestOf(category)) return best;
    if (!best) return category;
    return postedAt(newestOf(category)) > postedAt(newestOf(best))
      ? category
      : best;
  }, null);

  const hero = heroCategory ? take(heroCategory) : null;
  const slider = categories.map(take).filter(Boolean);
  const topStories = categories.map(take).filter(Boolean);

  return { hero, slider, topStories };
};
